import React from "react";

// Production-Ready Privacy Policy Page
// ------------------------------------
// 1. All relevant data is consolidated into one file for easy deployment.
// 2. Address updated to 5900 Balcones Drive Austin Texas 78734.
// 3. Styling is in a single string for quick copy-paste, but in a real production environment
//    consider using separate CSS or CSS Modules for better maintainability and performance.

const Privacy: React.FC = () => {
  // For production, ensure no console.log or debugging statements remain
  const lastUpdated = new Date("2024-03-20").toLocaleDateString();

  return (
    <>
      <style>{styles}</style>
      <main className="privacy-container">
        <article className="privacy-content">
          <header className="privacy-header">
            <h1 className="privacy-title">Privacy Policy</h1>
            <p className="last-updated">Last Updated: {lastUpdated}</p>
          </header>

          <section className="privacy-section" aria-labelledby="introduction">
            <h2 id="introduction" className="section-title">
              1. Introduction
            </h2>
            <p className="section-text">
              Welcome to FoodFax App ("we," "our," or "us"), a DBA of OnePace
              LLC. We are committed to protecting your privacy and ensuring the
              security of your personal information. This Privacy Policy
              explains how we collect, use, and protect your data when you use
              our food ingredient analysis service.
            </p>
          </section>

          <section
            className="privacy-section"
            aria-labelledby="information-collection"
          >
            <h2 id="information-collection" className="section-title">
              2. Information We Collect
            </h2>
            <ul className="privacy-list">
              <li>Photos of food products and their ingredients</li>
              <li>Device information and unique identifiers</li>
              <li>User preferences and dietary restrictions</li>
              <li>
                Payment information (processed securely through Apple's payment
                system)
              </li>
              <li>Usage data and interaction with our app</li>
            </ul>
          </section>

          <section
            className="privacy-section"
            aria-labelledby="information-usage"
          >
            <h2 id="information-usage" className="section-title">
              3. How We Use Your Information
            </h2>
            <ul className="privacy-list">
              <li>Provide personalized food analysis and recommendations</li>
              <li>Process your subscription payments</li>
              <li>Improve our ingredient analysis algorithms</li>
              <li>Send important updates about our service</li>
              <li>Ensure compliance with food safety regulations</li>
            </ul>
          </section>

          <section className="privacy-section" aria-labelledby="data-security">
            <h2 id="data-security" className="section-title">
              4. Data Storage and Security
            </h2>
            <p className="section-text">
              We implement industry-standard security measures to protect your
              data. Your information is stored on secure servers, and all data
              transmission is encrypted using TLS 1.3 protocols. We regularly
              update our security measures to maintain the safety of your
              information.
            </p>
          </section>

          <section className="privacy-section" aria-labelledby="subscription">
            <h2 id="subscription" className="section-title">
              5. Subscription and Payments
            </h2>
            <p className="section-text">
              Payments are processed securely through Apple's payment system. We
              do not store your complete payment information. Subscription
              management and billing are handled through your Apple ID account.
              For any payment-related inquiries, please contact Apple Support or
              manage your subscription through your device's settings.
            </p>
          </section>

          <section className="privacy-section" aria-labelledby="user-rights">
            <h2 id="user-rights" className="section-title">
              6. Your Rights
            </h2>
            <p className="section-text">You have the right to:</p>
            <ul className="privacy-list">
              <li>Access your personal data</li>
              <li>Request data deletion</li>
              <li>Opt-out of marketing communications</li>
              <li>Export your data</li>
              <li>Lodge a complaint with a supervisory authority</li>
            </ul>
          </section>

          <section className="privacy-section" aria-labelledby="contact">
            <h2 id="contact" className="section-title">
              7. Contact Us
            </h2>
            <address className="contact-info">
              For privacy-related concerns, please contact us at:
              <br />
              <a href="mailto:emma@foodfax.lol" className="contact-link">
                emma@foodfax.lol
              </a>
              <br />
              <strong>OnePace LLC dba FoodFax App</strong>
              <br />
              5900 Balcones Drive
              <br />
              Austin, Texas 78734
              <br />
              United States
            </address>
          </section>

          <footer className="privacy-footer">
            <p className="copyright">
              © {new Date().getFullYear()} OnePace LLC dba FoodFax App. All
              rights reserved.
            </p>
          </footer>
        </article>
      </main>
    </>
  );
};

export default Privacy;

// In a real production environment, consider placing this CSS in its own file (e.g. Privacy.module.css).
// This inline approach is purely for convenience. For best practices, avoid storing large CSS strings inline.
const styles = `
.privacy-container {
  max-width: 800px;
  margin: 2rem auto;
  padding: 0 1.5rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  color: #1a1a1a;
  line-height: 1.6;
}

.privacy-content {
  background: #ffffff;
  padding: 2.5rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
}

.privacy-header {
  text-align: center;
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #e5e7eb;
}

.privacy-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
  letter-spacing: -0.025em;
}

.last-updated {
  color: #6b7280;
  font-size: 0.875rem;
}

.privacy-section {
  margin-bottom: 2.5rem;
  padding-bottom: 2.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.privacy-section:last-of-type {
  border-bottom: none;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1.25rem;
  scroll-margin-top: 100px;
}

.section-text {
  color: #374151;
  margin-bottom: 1.25rem;
}

.privacy-list {
  list-style: none;
  padding-left: 1.5rem;
  margin: 1rem 0;
}

.privacy-list li {
  position: relative;
  padding-left: 1.5rem;
  margin-bottom: 0.75rem;
  color: #374151;
}

.privacy-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #6b7280;
}

.contact-info {
  font-style: normal;
  line-height: 1.8;
  background: #f9fafb;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;
}

.contact-link {
  color: #2563eb;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.15s ease;
}

.contact-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.privacy-footer {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
  text-align: center;
}

.copyright {
  color: #6b7280;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .privacy-container {
    margin: 1rem auto;
    padding: 0 1rem;
  }

  .privacy-content {
    padding: 1.5rem;
  }

  .privacy-title {
    font-size: 2rem;
  }

  .section-title {
    font-size: 1.25rem;
  }
}

@media print {
  .privacy-container {
    margin: 0;
    padding: 0;
  }

  .privacy-content {
    box-shadow: none;
    padding: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  * {
    animation: none !important;
    transition: none !important;
  }
}
`;
